/**
 * DashboardEscopo — KPIs, status breakdown and region pie chart
 * Renders above tabs in EscopoContagemPage
 */
import React, { useEffect, useState, useCallback } from 'react';
// recharts removido — regiões agora em tabela
import { BarChart3, AlertCircle } from 'lucide-react';
import escopoContagemService, {
  type DashboardResponse,
  type DropdownOption,
} from '../../services/escopoContagemService';
import { useRefetchOnFocus } from '../../hooks/useRefetchOnFocus';

interface Props {
  escopoId: number;
  mes: number;
  ano: number;
  refreshKey?: number;
}

const STATUS_LABELS: Record<string, string> = {
  AGENDADA: 'Agendada',
  PRE_AGENDADA: 'Pré-Agendada',
  EM_EXECUCAO: 'Em Execução',
  EXECUTADA: 'Executada',
  NAO_AGENDADO: 'Não Agendada',
  NAO_EXECUTADA: 'Não Executada',
  NAO_AUTORIZADA: 'Não Autorizada', // N21c
  CANCELADA: 'Cancelada',
};

const STATUS_COLORS: Record<string, string> = {
  AGENDADA: '#22c55e',
  PRE_AGENDADA: '#facc15',
  EM_EXECUCAO: '#3b82f6',
  EXECUTADA: '#10b981',
  NAO_AGENDADO: '#ef4444',
  NAO_EXECUTADA: '#f97316',
  NAO_AUTORIZADA: '#fb923c', // N21c
  CANCELADA: '#6b7280',
};

// Ordem lógica do fluxo de auditoria
const STATUS_ORDER = [
  'NAO_AGENDADO',
  'PRE_AGENDADA',
  'AGENDADA',
  'EM_EXECUCAO',
  'EXECUTADA',
  'NAO_EXECUTADA',
  'NAO_AUTORIZADA', // N21c
  'CANCELADA',
];

const REGION_COLORS = ['#3b82f6', '#ef4444', '#f59e0b', '#22c55e', '#8b5cf6', '#6b7280'];

const MESES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

export default function DashboardEscopo({ escopoId, mes, ano, refreshKey }: Props) {
  const [data, setData] = useState<DashboardResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [clienteId, setClienteId] = useState<number | undefined>();
  const [clientes, setClientes] = useState<DropdownOption[]>([]);

  // Load clientes dropdown
  useEffect(() => {
    escopoContagemService.dropdownClientes().then(setClientes).catch(() => {});
  }, []);

  // Load dashboard data
  const loadDashboard = useCallback(() => {
    if (!escopoId) return;
    setLoading(true);
    escopoContagemService
      .dashboardEscopo(escopoId, clienteId)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [escopoId, clienteId]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard, refreshKey]);

  // Recarrega ao voltar o foco pra aba/janela.
  useRefetchOnFocus(loadDashboard);

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 p-6 mb-4">
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
        </div>
      </div>
    );
  }

  if (!data || data.total_escopo === 0) return null;

  const statusOrdenado = [...data.status].sort(
    (a, b) => STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status)
  );

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 p-4 mb-3">
      {/* Header + client filter */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Dashboard — {MESES[mes - 1]} {ano}
          </h2>
        </div>
        <select
          value={clienteId ?? ''}
          onChange={(e) => setClienteId(e.target.value ? Number(e.target.value) : undefined)}
          className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white min-w-[200px]"
        >
          <option value="">Todos os Clientes</option>
          {clientes.map((c) => (
            <option key={c.id} value={c.id}>{c.nome}</option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Column 1: KPI Cards */}
        <div className="space-y-2">
          <div className="rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/30 p-3">
            <p className="text-sm text-blue-600 dark:text-blue-400 font-medium">Escopo</p>
            <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">{data.total_escopo}</p>
            <p className="text-xs text-blue-500 dark:text-blue-400">Total de contagens no mês</p>
          </div>
          <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 p-3">
            <div className="flex items-center gap-1.5">
              <AlertCircle className="w-4 h-4 text-red-500" />
              <p className="text-sm text-red-600 dark:text-red-400 font-medium">Pendente de Agendamento</p>
            </div>
            <p className="text-2xl font-bold text-red-700 dark:text-red-300">{data.pendente_agendamento}</p>
            <p className="text-xs text-red-500 dark:text-red-400">Hospitais ainda não agendados</p>
          </div>
        </div>

        {/* Column 2: Status Table (only shows status with qtde > 0) */}
        <div>
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Status das Auditorias</h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="text-left py-1 text-gray-500 dark:text-gray-400 font-medium">Status</th>
                <th className="text-right py-1 text-gray-500 dark:text-gray-400 font-medium">Qtde</th>
                <th className="text-right py-1 text-gray-500 dark:text-gray-400 font-medium">%</th>
              </tr>
            </thead>
            <tbody>
              {statusOrdenado.map((s) => (
                <tr key={s.status} className="border-b border-gray-100 dark:border-gray-700/50">
                  <td className="py-1 flex items-center gap-2">
                    <span
                      className="w-2.5 h-2.5 rounded-full inline-block"
                      style={{ backgroundColor: STATUS_COLORS[s.status] || '#6b7280' }}
                    />
                    <span className={s.qtde === 0 ? 'text-gray-400 dark:text-gray-500' : 'text-gray-900 dark:text-white'}>{STATUS_LABELS[s.status] || s.status}</span>
                  </td>
                  <td className={`text-right font-medium ${s.qtde === 0 ? 'text-gray-400 dark:text-gray-500' : 'text-gray-900 dark:text-white'}`}>{s.qtde}</td>
                  <td className="text-right text-gray-500 dark:text-gray-400">{s.percentual}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Column 3: Region Table */}
        <div>
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Distribuição por Região</h3>
          {data.regioes.length > 0 ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left py-1 text-gray-500 dark:text-gray-400 font-medium">Região</th>
                  <th className="text-right py-1 text-gray-500 dark:text-gray-400 font-medium">Qtde</th>
                  <th className="text-right py-1 text-gray-500 dark:text-gray-400 font-medium">%</th>
                </tr>
              </thead>
              <tbody>
                {data.regioes.map((r, idx) => (
                  <tr key={r.regiao} className="border-b border-gray-100 dark:border-gray-700/50">
                    <td className="py-1 flex items-center gap-2">
                      <span
                        className="w-2.5 h-2.5 rounded-full inline-block"
                        style={{ backgroundColor: REGION_COLORS[idx % REGION_COLORS.length] }}
                      />
                      <span className="text-gray-900 dark:text-white">{r.regiao}</span>
                    </td>
                    <td className="text-right text-gray-900 dark:text-white font-medium">{r.qtde}</td>
                    <td className="text-right text-gray-500 dark:text-gray-400">{r.percentual}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-sm text-gray-400 py-4 text-center">Sem dados de região</p>
          )}
        </div>
      </div>
    </div>
  );
}
